import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useCollaborationInvites } from '../../hooks/useCollaborationInvites';
import { respondToInvite } from '../../services/api';

export const CollaborationInvites: React.FC = () => {
  const { invites, loading, error } = useCollaborationInvites();
  const [projectNames, setProjectNames] = useState<Record<string, string>>({});
  const [respondingInviteId, setRespondingInviteId] = useState<string | null>(null);
  const [responseError, setResponseError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch project names for all invites
  useEffect(() => {
    const fetchProjectNames = async () => {
      const names: Record<string, string> = {};

      for (const invite of invites) {
        if (!names[invite.projectId]) {
          try {
            const projectDoc = await getDoc(doc(db, 'projects', invite.projectId));
            if (projectDoc.exists()) {
              names[invite.projectId] = projectDoc.data().name || 'Unnamed Project';
            } else {
              names[invite.projectId] = 'Unknown Project';
            }
          } catch (err) {
            console.error(`Error fetching project ${invite.projectId}:`, err);
            names[invite.projectId] = 'Unknown Project';
          }
        }
      }

      setProjectNames(names);
    };

    if (invites.length > 0) {
      fetchProjectNames();
    }
  }, [invites]);

  // Handle accept invite
  const handleAccept = async (inviteId: string, projectName: string) => {
    setRespondingInviteId(inviteId);
    setResponseError(null);
    setSuccessMessage(null);

    try {
      await respondToInvite(inviteId, 'accept');
      setSuccessMessage(`You're now a collaborator on ${projectName}`);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Error accepting invite:', err);
      setResponseError('Failed to accept invitation. Please try again.');
    } finally {
      setRespondingInviteId(null);
    }
  };

  // Handle decline invite
  const handleDecline = async (inviteId: string) => {
    setRespondingInviteId(inviteId);
    setResponseError(null);
    setSuccessMessage(null);

    try {
      await respondToInvite(inviteId, 'decline');
    } catch (err) {
      console.error('Error declining invite:', err);
      setResponseError('Failed to decline invitation. Please try again.');
    } finally {
      setRespondingInviteId(null);
    }
  };

  // Format relative time
  const getRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else if (diffDays < 30) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    } else {
      return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(date);
    }
  };

  // Don't show section if no invites and not loading
  if (!loading && invites.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-xl font-semibold text-gray-900">Collaboration Invitations</h3>
      </div>

      <div className="px-6 py-6">
        {/* Loading State */}
        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            <p className="mt-2 text-gray-600">Loading invitations...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">{error.message}</p>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="ml-3 text-sm text-green-700">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Response Error */}
        {responseError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">{responseError}</p>
          </div>
        )}

        {/* Invites List */}
        {!loading && invites.length > 0 && (
          <div className="space-y-4">
            {invites.map((invite) => {
              const projectName = projectNames[invite.projectId] || 'Loading...';

              return (
                <div
                  key={invite.inviteId}
                  className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <svg className="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h4 className="text-base font-medium text-gray-900">{projectName}</h4>
                          <p className="text-sm text-gray-600">
                            From <span className="font-medium">{invite.fromUserEmail}</span>
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Sent {getRelativeTime(invite.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="ml-4 flex gap-2">
                      <button
                        onClick={() => handleAccept(invite.inviteId, projectName)}
                        disabled={respondingInviteId === invite.inviteId}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-500 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {respondingInviteId === invite.inviteId ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processing...
                          </>
                        ) : (
                          'Accept'
                        )}
                      </button>
                      <button
                        onClick={() => handleDecline(invite.inviteId)}
                        disabled={respondingInviteId === invite.inviteId}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && invites.length === 0 && (
          <div className="text-center py-8">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-500">No pending invitations</p>
          </div>
        )}
      </div>
    </div>
  );
};
